import React, {useRef, useState} from 'react';
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import { writtenStatementValidationSchema } from 'app/edozvola/cases/case-technical-inspection/model/case-technical-inspection.schema';
import { writtenStatementInitial } from 'app/edozvola/cases/case-technical-inspection/model/case-technical-inspection.model';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { Button, Input } from 'edozvola-frontend-lib/dist/ui/components';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { APP_DATE_FORMAT, MAX_FILE_SIZE, messages } from 'app/config/constants';
import OibInput from 'app/shared/oib/oib-input';
import { LegalEntityModel, PersonModel } from 'app/shared/oib/oib.model';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { getWrittenStatementFile } from 'app/edozvola/cases/case-technical-inspection/service/case-technical-inspection.service';
import {
  fetchWrittenStatementQuery,
  saveWrittenStatementQuery,
} from 'app/edozvola/cases/case-technical-inspection/service/case-technical-inspection.queries';
import { Loader } from 'app/shared/loader/loader';
import {
  fetchChamberMembersQuery,
  fetchStatementTypeQuery,
  fetchSubjectTypeQuery,
  fetchWorkTypeQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import { queryClient } from 'app/app';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import FileUpload from 'app/shared/file-upload/file-upload';
import { SUBJECT_TYPE_ENUM, WRITTEN_STATEMENT_TYPE_ENUM } from 'app/shared/enumeration/enums';
import { ChamberMemberCodebookModel } from 'app/shared/model/shared.model';
import { formatAddress } from 'app/shared/address-input/address-helper';
import AddressInput from 'app/shared/address-input/address-input';
import error = toast.error;
import { openFileInNewWindow } from 'app/shared/util/navigation-util';

export interface IFinalReportOfSupervisingEngineerFormProps {
  idWrittenStatement: string | null;
  setIdWrittenStatement: any;
  readOnly: boolean;
}

const WrittenStatementForm = (props: IFinalReportOfSupervisingEngineerFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();

  const {data: statementTypeOptions, isLoading: statementTypeOptionsLoading} = fetchStatementTypeQuery()
  const { data: workTypeOptions, isLoading: workTypeOptionsLoading } = fetchWorkTypeQuery();
  const {data: chamberMemberOptions, isLoading: chamberMemberOptionsLoading} = fetchChamberMembersQuery();

  const {data: writtenStatementData, isLoading: writtenStatementDataLoading} = fetchWrittenStatementQuery(props.idWrittenStatement)
  const {mutate: saveWrittenStatement, isPending: savingWrittenStatement} = saveWrittenStatementQuery(uuid, () => closeModal())

  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeQuery();

  const [previewAttachmentPending, setPreviewAttachmentPending] = useState<boolean>(false);

  const filteredSubjectTypeOptions = React.useMemo(() => {
    return subjectTypeOptions?.filter(option => [SUBJECT_TYPE_ENUM.OBRT, SUBJECT_TYPE_ENUM.FIZICKA_OSOBA, SUBJECT_TYPE_ENUM.PRAVNA_OSOBA].includes(option.id))
  }, [subjectTypeOptions]);

  const hiddenFileInput = useRef(null);

  const formik = useFormik({
    initialValues: writtenStatementData || writtenStatementInitial,
    validationSchema: writtenStatementValidationSchema,
    onSubmit: (values) => saveWrittenStatement(values),
    enableReinitialize: true,
    validateOnMount: true,
    initialTouched: {
      person: {
        oib: !writtenStatementData,
      },
      legalEntity: {
        oib: !writtenStatementData,
      },
      siteConstructorAddress: {
        cityMunicipality: true,
        community: true,
        postalCode: true,
        street: true,
      },
    },
  });

  const onChamberMemberChanged = (selected: ChamberMemberCodebookModel[]) => {
    formik.setFieldValue('chiefConstructionEngineer.oib', selected[0] ? selected[0].oib : '');
    formik.setFieldValue('chiefConstructionEngineer.authorizationNumber', selected);
  };

  const statementTypeEquals = (statementType: WRITTEN_STATEMENT_TYPE_ENUM) =>
    formik.values.writtenStatementType[0]?.id === statementType;

  const closeModal = () => {
    queryClient.invalidateQueries({ queryKey: ['written-statement'] });
    props.setIdWrittenStatement(null);
  };

  const onOibChanged = (value: string) => {
    formik.setFieldValue('oibFetched', false);
    formik.setFieldValue('person.address', null);
    formik.setFieldValue('person.firstName', '');
    formik.setFieldValue('person.lastName', '');
    formik.setFieldValue('legalEntity.companyName', '');
    formik.setFieldValue('legalEntity.address', null);

    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT)) {
      formik.setTouched({
        ...formik.touched,
        legalEntity: {
          oib: true,
        },
      });
      formik.setFieldValue('legalEntity.oib', value);
    } else {
      formik.setTouched({
        ...formik.touched,
        person: {
          oib: true,
        },
      });
      formik.setFieldValue('person.oib', value);
    }
  };

  const onStatementTypeChanged = (statementType) => {
    if (statementType[0]?.id === WRITTEN_STATEMENT_TYPE_ENUM.PISANA_IZJAVA_IZVODACA) {
      formik.setFieldValue('chiefConstructionEngineer.oib', '');
      formik.setFieldValue('chiefConstructionEngineer.authorizationNumber', []);
    }
    formik.setFieldValue('writtenStatementType', statementType);
  }

  const oibFetched = (fetchedOibData: PersonModel | LegalEntityModel) => {
    formik.setFieldValue('oibFetched', true);
    if (isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA)) {
      formik.setFieldValue('person', fetchedOibData);
    } else {
      formik.setFieldValue('legalEntity', fetchedOibData);
    }
  };

  const selectFile = () => {
    hiddenFileInput.current.click();
  };

  const extensionAllowed = (fileName: string) => {
    return fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) === 'pdf';
  };

  const appendFile = e => {
    const selectedFile = e.target.files[0];
    if (selectedFile.size > MAX_FILE_SIZE) {
      showToast(toast.error, messages.FILE_TOO_LARGE);
    } else if (!extensionAllowed(selectedFile.name)) {
      showToast(toast.error, messages.FILE_NOT_PDF);
    } else {
      formik.setFieldValue('multipartFile', selectedFile);
      formik.setFieldValue('fileWrapper.changed', true);
    }
  };

  const downloadFile = () => {
    if (formik.values.fileWrapper) {
      setPreviewAttachmentPending(true);
      getWrittenStatementFile(props.idWrittenStatement)
        .then(data => {
          if (data) {
            openFileInNewWindow(data.url)
          }
        })
        .catch(err => showToast(error, 'Dogodila se greška prilikom preuzimanja dokumenta.'))
        .finally(() => setPreviewAttachmentPending(false));
    }
  };

  const isOsobaSelectedEqual = (subjectType: SUBJECT_TYPE_ENUM) =>
    formik.values.subjectType && formik.values.subjectType[0] && formik.values.subjectType[0].id === subjectType;

  const isPravnaIliFizickaOsobaSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA);

  const onSubjectTypeChange = selected => {
    onOibChanged('');
    formik.setFieldValue('legalEntity.mb', '');
    formik.setFieldValue('subjectType', selected);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  return (
    <>
      {writtenStatementDataLoading && <Loader />}
      {!writtenStatementDataLoading && (
        <Modal scrollable={true} isOpen={props.idWrittenStatement !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled podataka o pisanoj izjavi izvođača</ModalHeader>
          ) : (
            <ModalHeader>
              {props.idWrittenStatement === '' ? 'Unos pisane izjave izvođača' : 'Uređivanje pisane izjave izvođača'}
            </ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <div className={'form-header'}>Pisana izjava izvođača</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta izjave'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu izjave'}
                      onItemSelect={onStatementTypeChanged}
                      selected={formik.values.writtenStatementType}
                      labelKey={'value'}
                      options={statementTypeOptionsLoading ? [] : statementTypeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.writtenStatementType as string}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Vrsta radova'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu radova'}
                      onItemSelect={selected => formik.setFieldValue('workType', selected)}
                      selected={formik.values.workType}
                      labelKey={'value'}
                      options={workTypeOptionsLoading ? [] : workTypeOptions}
                      disabled={props.readOnly}
                      error={formik.errors.workType as string}
                      showErrorMessage
                      clearButton
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Oznaka'} />
                    <Input
                      disabled={props.readOnly}
                      value={formik.values.mark}
                      error={formik.errors.mark ? `${formik.errors.mark}` : ''}
                      maxLength={100}
                      onChange={event => formik.setFieldValue('mark', event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Datum izrade'} />
                    <Field
                      id="statementDate"
                      name="statementDate"
                      render={({ field }) => (
                        <FormikDatepicker
                          field={field}
                          disabled={props.readOnly}
                          setFieldValue={formik.setFieldValue}
                          dateFormat={APP_DATE_FORMAT}
                          error={formik.errors.statementDate ? `${formik.errors.statementDate}` : ''}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Dokument'} />
                    {props.readOnly ? (
                      <Input
                        disabled={props.readOnly}
                        onClick={selectFile}
                        value={formik.values.multipartFile ? formik.values.multipartFile.name : formik.values.fileWrapper?.fileName}
                      />
                    ) : (
                      <FileUpload
                        disabled={props.readOnly}
                        setFile={selectedFile => {
                          formik.setFieldValue('multipartFile', selectedFile);
                          formik.setFieldValue('fileWrapper.changed', true);
                        }}
                        value={formik.values.multipartFile ? formik.values.multipartFile.name : formik.values.fileWrapper?.fileName ?? ''}
                        appendIcon={formik.values.fileWrapper?.fileName ? 'download' : 'paperclip'}
                        appendOnClick={formik.values.fileWrapper?.fileName ? downloadFile : undefined}
                        clearButton
                        onClear={() => {
                          formik.setFieldValue('multipartFile', null);
                          formik.setFieldValue('fileWrapper.changed', true);
                          formik.setFieldValue('fileWrapper.fileName', '');
                        }}
                        previewAttachmentPending={previewAttachmentPending}
                      />
                    )}
                  </Col>
                  <input type="file" ref={hiddenFileInput} onChange={appendFile} className="hidden-upload" />
                </Row>
                <hr />
                <div className={'form-header'}>Izvođač</div>
                <div className={'d-flex justify-content-between flex-column gap-3'}>
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Vrsta subjekta'} />
                      <PaginatedDropdown
                        placeholder={!props?.readOnly && 'Odaberi vrstu subjekta'}
                        onItemSelect={onSubjectTypeChange}
                        selected={formik.values.subjectType}
                        labelKey={'value'}
                        options={filteredSubjectTypeOptions}
                        isLoading={subjectTypeOptionsLoading}
                        disabled={props.readOnly}
                        error={(formik.errors.subjectType as string) ?? ''}
                        showErrorMessage
                      />
                    </Col>
                    <Col md={6}>
                      {isPravnaIliFizickaOsobaSelected ? (
                        <OibInput
                          value={
                            isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)
                              ? formik.values.legalEntity?.oib
                              : formik.values.person?.oib
                          }
                          handleBlur={formik.handleBlur}
                          handleChange={event => onOibChanged(event.target.value)}
                          errors={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.errors.legalEntity : formik.errors.person}
                          touched={
                            isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? formik.touched.legalEntity : formik.touched.person
                          }
                          onOibFetched={oibFetched}
                          fetchLegalEntity={isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                          disabled={props.readOnly || !formik.values.subjectType || formik.values.subjectType.length === 0}
                        />
                      ) : (
                        isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT) && (
                          <>
                            <Label labelText="MB" />
                            <Input
                              value={formik.values.legalEntity?.mb}
                              id="mb"
                              name="mb"
                              maxLength={11}
                              disabled={props.readOnly}
                              onChange={event => formik.setFieldValue('legalEntity.mb', event.target.value)}
                              error={formik.errors.legalEntity && formik.errors.legalEntity['mb'] ? formik.errors.legalEntity['mb'] : ''}
                            />
                          </>
                        )
                      )}
                    </Col>
                  </Row>
                  {isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.FIZICKA_OSOBA) && (
                    <Row>
                      <Col md={6}>
                        <Label labelText={'Ime'} />
                        <Input
                          disabled
                          value={formik.values.person?.firstName}
                          error={formik.errors.person && formik.errors.person['firstName'] ? formik.errors.person['firstName'] : ''}
                          maxLength={200}
                          onChange={event => formik.setFieldValue('person.firstName', event.target.value)}
                        />
                      </Col>
                      <Col md={6}>
                        <Label labelText={'Prezime'} />
                        <Input
                          disabled
                          value={formik.values.person?.lastName}
                          error={formik.errors.person && formik.errors.person['lastName'] ? formik.errors.person['lastName'] : ''}
                          maxLength={200}
                          onChange={event => formik.setFieldValue('person.lastName', event.target.value)}
                        />
                      </Col>
                    </Row>
                  )}
                  {(isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT)) && (
                    <Row>
                      <Col md={6}>
                        <Label labelText={'Naziv'} />
                        <Input
                          disabled={props.readOnly || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                          value={formik.values.legalEntity?.companyName}
                          error={
                            formik.errors.legalEntity && formik.errors.legalEntity['companyName']
                              ? formik.errors.legalEntity['companyName']
                              : ''
                          }
                          maxLength={200}
                          onChange={event => formik.setFieldValue('legalEntity.companyName', event.target.value)}
                        />
                      </Col>
                    </Row>
                  )}
                  {isPravnaIliFizickaOsobaSelected && formik.values.person?.address && (
                    <Row>
                      <Col md={6}>
                        <Label labelText="Adresa iz OIB-a" />
                        <span>{formatAddress(formik.values.person.address)}</span>
                      </Col>
                    </Row>
                  )}

                  {isPravnaIliFizickaOsobaSelected && formik.values.legalEntity?.address && (
                    <Row>
                      <Col md={6}>
                        <Label labelText="Adresa iz OIB-a" />
                        <span>{formatAddress(formik.values.legalEntity.address)}</span>
                      </Col>
                    </Row>
                  )}
                  <AddressInput
                    field={formik.values.siteConstructorAddress}
                    errors={formik.errors.siteConstructorAddress}
                    readOnly={props.readOnly}
                    setFieldValue={(field: string, value: any) => {
                      formik.setFieldValue(`siteConstructorAddress.${field}`, value).then(() => formik.validateForm());
                    }}
                  />
                </div>
                {statementTypeEquals(WRITTEN_STATEMENT_TYPE_ENUM.PISANA_IZJAVA_GLAVNOG_IZVODACA) && (
                  <>
                    <hr />
                    <div className={'form-header'}>Odgovorna osoba</div>
                    <Row>
                      <Col md={6}>
                        <Label labelText={'Broj ovlaštenja/Ime i prezime'} />
                        <PaginatedDropdown
                          placeholder="Odaberi broj ovlaštenja/ime i prezime"
                          onItemSelect={onChamberMemberChanged}
                          selected={formik.values.chiefConstructionEngineer?.authorizationNumber}
                          labelKey={'value'}
                          options={chamberMemberOptionsLoading ? [] : chamberMemberOptions}
                          disabled={props.readOnly}
                          clearButton
                          error={
                            !props.readOnly &&
                            formik.errors.chiefConstructionEngineer &&
                            formik.errors.chiefConstructionEngineer['authorizationNumber']
                              ? (formik.errors.chiefConstructionEngineer['authorizationNumber'] as string)
                              : ''
                          }
                          showErrorMessage
                        />
                      </Col>
                      <Col md={6}>
                        <Label labelText={'OIB'} />
                        <Input
                          disabled
                          error={
                            !props.readOnly && formik.errors.chiefConstructionEngineer && formik.errors.chiefConstructionEngineer['oib']
                              ? formik.errors.chiefConstructionEngineer['oib']
                              : ''
                          }
                          value={formik.values.chiefConstructionEngineer.oib}
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  disabled={!formik.dirty || !formik.isValid}
                  isLoading={savingWrittenStatement}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}

export default WrittenStatementForm
